import { useAuth0 } from '@auth0/auth0-react'
import { Navigate, Routes as ReactRouterRoutes, Route } from 'react-router-dom'

import { Activiteiten } from '../../aansluiting/activiteiten/pages/Activiteiten'
import { AssignedBijlagen } from '../../aansluiting/bijlagen/pages/AssignedBijlagen'
import { Contractinstellingen } from '../../aansluiting/contractinstellingen/pages/Contractinstellingen'
import { Dashboard } from '../../aansluiting/dashboard/pages/Dashboard'
import { Project } from '../../aansluiting/project/pages/Project'
import { Projecten } from '../../aansluiting/projecten/pages/Projecten'
import { Subprocessen } from '../../aansluiting/subprocessen/pages/Subprocessen'
import { Landing } from '../../auth/components/Landing'
import { Loading } from '../../auth/components/Loading'
import { LoginFailure } from '../../auth/components/LoginFailure'
import { ProtectedRoute } from '../../auth/components/ProtectedRoute'
import { useAuthenticatedUser } from '../../auth/hooks/useAuthenticatedUser'
import { Beheer } from '../../beheer/pages/Beheer'
import { Activiteiten as HoofdleidingActiviteiten } from '../../hoofdleiding/activiteiten/pages/Activiteiten'
import { AssignedBijlagen as HoofdleidingAssignedBijlagen } from '../../hoofdleiding/bijlagen/pages/AssignedBijlagen'
import { Contractinstellingen as HoofdleidingContractinstellingen } from '../../hoofdleiding/contractinstellingen/pages/Contractinstellingen'
import { Dashboard as HoofdleidingDashboard } from '../../hoofdleiding/dashboard/pages/Dashboard'
import { Planning as Balkenplanning } from '../../hoofdleiding/planning/pages/Planning'
import { Project as HoofdleidingProject } from '../../hoofdleiding/project/pages/Project'
import { Projecten as HoofdleidingProjecten } from '../../hoofdleiding/projecten/pages/Projecten'
import { Subprocessen as HoofdleidingSubprocessen } from '../../hoofdleiding/subprocessen/pages/Subprocessen'
import { DomainSelector } from '../../nav/components/DomainSelector'
import { NotFound } from '../../nav/components/NotFound'
import { CombiOpdracht } from '../../opdracht/combi/pages/CombiOpdracht'
import { HoofdnetOpdracht } from '../../opdracht/hoofdnet/pages/HoofdnetOpdracht'
import { Opdracht } from '../../opdracht/pages/Opdracht'
import { CombiOpdrachten } from '../../opdrachten/combi/pages/CombiOpdrachten'
import { HoofdnetOpdrachten } from '../../opdrachten/hoofdnet/pages/HoofdnetOpdrachten'
import { Opdrachten } from '../../opdrachten/pages/Opdrachten'
import { Planning } from '../../planning/pages/Planning'
import { Projectdossier } from '../../projectdossier/pages/Projectdossier'
import { Projectdossiers } from '../../projectdossiers/pages/Projectdossiers'
import { Projectmelding } from '../../projectmelding/pages/Projectmelding'
import { Projectmeldingen } from '../../projectmeldingen/pages/Projectmeldingen'
import { Layout } from '../../theme/components/Layout'

export { Routes }

const Routes = () => {
  const { isAuthenticated, isLoading, error } = useAuth0()
  const {
    canViewDashboard,
    canViewProjecten,
    canViewCombiOpdrachten,
    canViewOpdrachten,
    canViewHoofdnetOpdrachten,
    canViewActiviteiten,
    canViewProjectmeldingen,
    canViewProjectdossiers,
    canViewBeheer,
    canViewAssignedBijlagen,
    hasPermissions,
    canViewBalkenplanning,
  } = useAuthenticatedUser()

  const isLoadingPermissions = !hasPermissions && isAuthenticated

  if (isLoadingPermissions || isLoading) {
    return <Loading />
  }

  if (error) {
    return <LoginFailure error={error} />
  }

  const routeTree = [
    {
      path: '/',
      element: <Navigate to={'/home'} replace />,
    },
    {
      path: '/home',
      element: hasPermissions ? (
        <DomainSelector
          canViewProjecten={canViewProjecten}
          canViewOpdrachten={canViewOpdrachten}
          canViewHoofdnetOpdrachten={canViewHoofdnetOpdrachten}
        />
      ) : (
        <Landing />
      ),
    },
    {
      path: '/aansluiting/dashboard',
      element: (
        <ProtectedRoute allowed={canViewDashboard}>
          <Dashboard />
        </ProtectedRoute>
      ),
    },
    {
      path: '/hoofdleiding/dashboard',
      element: (
        <ProtectedRoute allowed={canViewProjecten}>
          <HoofdleidingDashboard />
        </ProtectedRoute>
      ),
    },
    {
      path: '/hoofdleiding/projecten',
      element: (
        <ProtectedRoute allowed={canViewProjecten}>
          <HoofdleidingProjecten />
        </ProtectedRoute>
      ),
    },
    {
      path: '/hoofdleiding/planning',
      element: (
        <ProtectedRoute allowed={canViewBalkenplanning}>
          <Balkenplanning />
        </ProtectedRoute>
      ),
    },
    {
      path: '/hoofdleiding/activiteiten',
      element: (
        <ProtectedRoute allowed={canViewProjecten}>
          <HoofdleidingActiviteiten />
        </ProtectedRoute>
      ),
    },
    {
      path: '/hoofdleiding/bijlagen',
      element: (
        <ProtectedRoute allowed={canViewProjecten}>
          <HoofdleidingAssignedBijlagen />
        </ProtectedRoute>
      ),
    },
    {
      path: '/hoofdleiding/subprocessen',
      element: (
        <ProtectedRoute allowed={canViewAssignedBijlagen}>
          <HoofdleidingSubprocessen />
        </ProtectedRoute>
      ),
    },
    {
      path: '/hoofdleiding/contractinstellingen',
      element: (
        <ProtectedRoute allowed={canViewProjecten}>
          <HoofdleidingContractinstellingen />
        </ProtectedRoute>
      ),
    },
    {
      path: '/hoofdleiding/project/:id',
      element: (
        <ProtectedRoute allowed={canViewProjecten}>
          <HoofdleidingProject />
        </ProtectedRoute>
      ),
    },
    {
      path: '/aansluiting/projecten',
      element: (
        <ProtectedRoute allowed={canViewProjecten}>
          <Projecten />
        </ProtectedRoute>
      ),
    },
    {
      path: '/aansluiting/project/:id',
      element: (
        <ProtectedRoute allowed={canViewProjecten}>
          <Project />
        </ProtectedRoute>
      ),
    },
    {
      path: '/opdrachten',
      element: (
        <ProtectedRoute allowed={canViewOpdrachten}>
          <Opdrachten />
        </ProtectedRoute>
      ),
    },
    {
      path: '/opdracht/:id',
      element: (
        <ProtectedRoute allowed={canViewOpdrachten}>
          <Opdracht />
        </ProtectedRoute>
      ),
    },
    {
      path: '/combiopdrachten',
      element: (
        <ProtectedRoute allowed={canViewCombiOpdrachten}>
          <CombiOpdrachten />
        </ProtectedRoute>
      ),
    },
    {
      path: '/combiopdracht/:id',
      element: (
        <ProtectedRoute allowed={canViewCombiOpdrachten}>
          <CombiOpdracht />
        </ProtectedRoute>
      ),
    },
    {
      path: '/aansluiting/activiteiten',
      element: (
        <ProtectedRoute allowed={canViewActiviteiten}>
          <Activiteiten />
        </ProtectedRoute>
      ),
    },
    {
      path: '/aansluiting/subprocessen',
      element: (
        <ProtectedRoute allowed={canViewAssignedBijlagen}>
          <Subprocessen />
        </ProtectedRoute>
      ),
    },
    {
      path: '/aansluiting/bijlagen',
      element: (
        <ProtectedRoute allowed={canViewAssignedBijlagen}>
          <AssignedBijlagen />
        </ProtectedRoute>
      ),
    },
    {
      path: '/aansluiting/contractinstellingen',
      element: (
        <ProtectedRoute allowed={canViewProjecten}>
          <Contractinstellingen />
        </ProtectedRoute>
      ),
    },
    {
      path: '/vooraanmeldingen',
      element: (
        <ProtectedRoute allowed={canViewProjectmeldingen}>
          <Projectmeldingen />
        </ProtectedRoute>
      ),
    },
    {
      path: '/vooraanmelding/:id',
      element: (
        <ProtectedRoute allowed={canViewProjectmeldingen}>
          <Projectmelding />
        </ProtectedRoute>
      ),
    },
    {
      path: '/projectdossiers',
      element: (
        <ProtectedRoute allowed={canViewProjectdossiers}>
          <Projectdossiers />
        </ProtectedRoute>
      ),
    },
    {
      path: '/projectdossier/:id',
      element: (
        <ProtectedRoute allowed={canViewProjectdossiers}>
          <Projectdossier />
        </ProtectedRoute>
      ),
    },
    {
      path: '/beheer',
      element: (
        <ProtectedRoute allowed={canViewBeheer}>
          <Beheer />
        </ProtectedRoute>
      ),
    },
    {
      path: '/planning',
      element: (
        <ProtectedRoute allowed={canViewProjecten}>
          <Planning />
        </ProtectedRoute>
      ),
    },
    {
      path: '/hoofdnetopdrachten',
      element: (
        <ProtectedRoute allowed={canViewHoofdnetOpdrachten}>
          <HoofdnetOpdrachten />
        </ProtectedRoute>
      ),
    },
    {
      path: '/hoofdnetopdracht/:id',
      element: (
        <ProtectedRoute allowed={canViewHoofdnetOpdrachten}>
          <HoofdnetOpdracht />
        </ProtectedRoute>
      ),
    },

    {
      path: '*',
      element: <NotFound />,
    },
  ] as const

  return (
    <Layout>
      <ReactRouterRoutes>
        {routeTree.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
      </ReactRouterRoutes>
    </Layout>
  )
}
